import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { appStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { AppIcon, ICON_SIZES } from '@backrs_ui/common-ui/src/designSystem/icons/AppIcon';
import { SIDEBAR_COLLAPSED_WIDTH } from '@backrs_ui/common-ui/src/molecules/BackrsSidebar/BackrsSidebar';
import { Box, Typography } from '@mui/material';
import { getVisiblePages } from './pagination.util';
var BUTTON_STRINGS = appStrings.buttonText;
var PageButton = function (_a) {
    var display = _a.display, currentPage = _a.currentPage, onClick = _a.onClick;
    if (display.type === 'ELLIPSIS') {
        return _jsx(Box, { sx: getPageButtonStyles(false, true), children: "..." });
    }
    return (_jsx(Box, { sx: getPageButtonStyles(display.pageNumber === currentPage, false), onClick: function () { return onClick(display.pageNumber); }, children: _jsx(Typography, { variant: "text-sm-medium", color: display.pageNumber === currentPage
                ? 'Colors/Text/text-secondary-hover'
                : 'Colors/Text/text-tertiary', children: display.pageNumber }) }));
};
var NAVIGATION_BUTTONS = {
    previous: {
        text: BUTTON_STRINGS.previous,
        iconName: 'ArrowLeft',
        iconPosition: 'left',
    },
    next: {
        text: BUTTON_STRINGS.next,
        iconName: 'ArrowRight',
        iconPosition: 'right',
    },
    back: {
        text: BUTTON_STRINGS.back,
        iconPosition: 'left',
    },
    finish: {
        text: BUTTON_STRINGS.finish,
        iconPosition: 'right',
    },
};
var NavigationButton = function (_a) {
    var buttonType = _a.buttonType, onClick = _a.onClick, disabled = _a.disabled, screenSize = _a.screenSize;
    var config = NAVIGATION_BUTTONS[buttonType];
    var textIconColor = disabled ? 'Colors/Text/text-disabled' : 'Colors/Text/text-tertiary';
    return (_jsxs(Box, { sx: previousNextButtonStyles(disabled, screenSize), onClick: disabled ? undefined : onClick, children: [config.iconPosition === 'left' &&
                (config.iconName ? (_jsx(AppIcon, { iconName: config.iconName, diameter: "20px", color: textIconColor })) : (_jsx(Box, { sx: { width: '20px' } }))), _jsx(Typography, { variant: "text-sm-medium", color: function (_a) {
                    var backrs = _a.backrs;
                    return backrs.colors[textIconColor];
                }, children: config.text }), config.iconPosition === 'right' &&
                (config.iconName ? (_jsx(AppIcon, { iconName: config.iconName, diameter: "20px", color: textIconColor })) : (_jsx(Box, { sx: { width: '20px' } })))] }));
};
export var Pagination = function (_a) {
    var currentPage = _a.currentPage, totalPages = _a.totalPages, onPageChange = _a.onPageChange, handlePrevPage = _a.handlePrevPage, handleNextPage = _a.handleNextPage, _b = _a.withPageNumbers, withPageNumbers = _b === void 0 ? true : _b, finishButtonOnClick = _a.finishButtonOnClick, backButtonOnClick = _a.backButtonOnClick, maxWidth = _a.maxWidth;
    var isFirstPage = currentPage === 1;
    var isLastPage = currentPage === totalPages;
    var prevButtonType = isFirstPage && backButtonOnClick ? 'back' : 'previous';
    var nextButtonType = isLastPage && finishButtonOnClick ? 'finish' : 'next';
    var prevButtonHandler = isFirstPage ? backButtonOnClick : handlePrevPage;
    var nextButtonHandler = isLastPage ? finishButtonOnClick : handleNextPage;
    var prevButtonDisabled = isFirstPage && !backButtonOnClick;
    var nextButtonDisabled = isLastPage && !finishButtonOnClick;
    return (_jsxs(Box, { sx: paginationContainerStyles(maxWidth), children: [_jsx(NavigationButton, { buttonType: prevButtonType, onClick: prevButtonHandler, disabled: prevButtonDisabled, screenSize: "large" }), _jsx(Box, { sx: pageButtonsContainerStyles, children: withPageNumbers &&
                    getVisiblePages(currentPage, totalPages).map(function (page, index) { return (_jsx(PageButton, { display: page, currentPage: currentPage, onClick: onPageChange }, "page-".concat(page, "-").concat(index))); }) }), _jsx(NavigationButton, { buttonType: nextButtonType, onClick: nextButtonHandler, disabled: nextButtonDisabled, screenSize: "large" }), _jsxs(Box, { sx: smallScreenPaginationContainerStyles, children: [_jsx(NavigationButton, { buttonType: prevButtonType, onClick: prevButtonHandler, disabled: prevButtonDisabled, screenSize: "small" }), _jsx(NavigationButton, { buttonType: nextButtonType, onClick: nextButtonHandler, disabled: nextButtonDisabled, screenSize: "small" })] })] }));
};
var paginationContainerStyles = function (maxWidth) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            display: 'flex',
            gap: backrs.spacing['spacing-md'],
            justifyContent: {
                xs: 'flex-start',
                sm: 'space-between',
            },
            alignItems: {
                xs: 'flex-start',
                sm: 'center',
            },
            width: {
                xs: "calc(100% - ".concat(SIDEBAR_COLLAPSED_WIDTH, "px)"),
                md: "calc(100% - ".concat(SIDEBAR_COLLAPSED_WIDTH, "px - 56px - 56px + 2px)"),
                lg: "calc(100% - ".concat(SIDEBAR_COLLAPSED_WIDTH, "px - 56px - 56px + 2px)"),
            },
            '@media screen and (max-width: 1024px) and (max-height: 768px)': {
                width: "calc(100% - ".concat(SIDEBAR_COLLAPSED_WIDTH, "px)"),
                left: '72px',
                px: '12px',
            },
            '@media screen and (max-width: 1205px) and (max-height: 768px) and (min-width: 1200px)': {
                marginLeft: "".concat(SIDEBAR_COLLAPSED_WIDTH, "px"),
                width: "calc(100% - ".concat(SIDEBAR_COLLAPSED_WIDTH, "px)"),
                paddingRight: backrs.spacing['spacing-lg'],
            },
            px: {
                xs: backrs.spacing['spacing-md'],
                sm: backrs.spacing['spacing-4xl'],
                md: backrs.spacing['spacing-none'],
            },
            pb: {
                xs: backrs.spacing['spacing-lg'],
                sm: backrs.spacing['spacing-xl'],
                md: backrs.spacing['spacing-4xl'],
                lg: backrs.spacing['spacing-5xl'],
            },
            pt: {
                xs: backrs.spacing['spacing-lg'],
                sm: backrs.spacing['spacing-xl'],
                md: backrs.spacing['spacing-2xl'],
                lg: backrs.spacing['spacing-3xl'],
            },
            '@media (max-height: 768px)': {
                pb: backrs.spacing['spacing-2xl'],
            },
            borderTop: "1px solid ".concat(backrs.colors['Colors/Border/border-secondary']),
            backgroundColor: backrs.colors['Beige/25'],
            boxSizing: 'border-box',
            zIndex: 2,
            position: 'fixed',
            bottom: 0,
            flexDirection: {
                xs: 'column',
                sm: 'row',
            },
            left: {
                xs: "".concat(SIDEBAR_COLLAPSED_WIDTH, "px"),
                md: "calc(".concat(SIDEBAR_COLLAPSED_WIDTH, "px + 56px - 1px)"),
                lg: 'unset',
            },
            maxWidth: maxWidth ? "".concat(maxWidth - 64 - 64, "px") : '100%',
            flexWrap: 'wrap',
        });
    };
};
var pageButtonsContainerStyles = function (_a) {
    var backrs = _a.backrs;
    return ({
        display: 'flex',
        alignItems: 'center',
        gap: backrs.spacing['spacing-xs'],
        alignSelf: 'center',
        flexWrap: 'wrap',
        minHeight: ICON_SIZES['32px'],
        height: ICON_SIZES['32px'],
    });
};
var getPageButtonStyles = function (isCurrentPage, isEllipsis) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            width: ICON_SIZES['32px'],
            height: ICON_SIZES['32px'],
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            borderRadius: backrs.radius['radius-full'],
            cursor: isEllipsis || isCurrentPage ? 'default' : 'pointer',
            backgroundColor: isCurrentPage
                ? backrs.colors['Beige/100']
                : backrs.colors['Colors/Base/Transparent'],
            userSelect: 'none',
        });
    };
};
var previousNextButtonStyles = function (isDisabled, screenSize) {
    return function (_a) {
        var backrs = _a.backrs;
        return ({
            justifyContent: 'center',
            alignItems: 'center',
            cursor: isDisabled ? 'default' : 'pointer',
            gap: backrs.spacing['spacing-sm'],
            userSelect: 'none',
            display: {
                xs: screenSize === 'large' ? 'none' : 'flex',
                sm: screenSize === 'large' ? 'flex' : 'none',
            },
        });
    };
};
var smallScreenPaginationContainerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    boxSizing: 'border-box',
    flexWrap: 'wrap',
};
